// Live data hooks (EPG01–EPG03, XC04). Owner: WP04.
// Channels: GET /api/catalog?kind=live paged (250/page) and loaded progressively in the background, rendering from page 1.
// Guide: GET /api/guide for the channels near the viewport only, ≤50 channels and ≤24 h per request (no request storm).
import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatalogResponse, Category, Channel, GuideResponse, Preferences, Program } from '@tuned/contracts';
import { api, qs } from '../../app/api.ts';
import { toast } from '../../components/index.ts';
import { useUserState } from '../../store/index.ts';

export const PAGE = 250;
export const GUIDE_BLOCK = 50;

export function useLiveChannels(connectionId: string | undefined) {
  const q = useInfiniteQuery({
    queryKey: ['catalog', connectionId, 'live', 'number'],
    enabled: !!connectionId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<CatalogResponse>('GET', `/api/catalog${qs({ connectionId, kind: 'live', sort: 'number', limit: PAGE, cursor: pageParam })}`, undefined, { signal }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 5 * 60_000,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = q;
  const pages = q.data?.pages.length ?? 0; // re-run per page: fast responses can batch the fetching flag away
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage, pages]);
  const channels = useMemo(
    () => (q.data?.pages ?? []).flatMap((p) => p.items).filter((i): i is Channel => i.kind === 'live'),
    [q.data],
  );
  const first = q.data?.pages[0];
  return { query: q, channels, total: first?.total ?? null, complete: !!q.data && !hasNextPage, freshness: first?.freshness ?? null };
}

export function useLiveCategories(connectionId: string | undefined) {
  return useQuery({
    queryKey: ['categories', connectionId, 'live'],
    enabled: !!connectionId,
    queryFn: () => api<{ revision: string | null; categories: Category[] }>('GET', `/api/categories${qs({ connectionId, kind: 'live' })}`),
    staleTime: 10 * 60_000,
  });
}

/** `categories`: any-of category ids for the quick chips (Sports, Movies); `restart`: only channels advertising replay (02_Live_Guide "Can restart"). */
export type ChannelFilter = { mode: 'all' | 'favorites'; category: string | null; categories?: string[] | null; restart?: boolean };

/** Filters and orders channels: hidden/excluded removed, favorites/category applied, custom order first (EPG01, SAFE01). */
export function arrangeChannels(
  channels: Channel[],
  filter: ChannelFilter,
  prefs: Pick<Preferences, 'channelOrder' | 'hiddenCategoryIds'> | null,
  isInList: (list: 'favorite' | 'hidden', id: string) => boolean,
): Channel[] {
  const hiddenCats = new Set(prefs?.hiddenCategoryIds ?? []);
  const order = new Map((prefs?.channelOrder ?? []).map((id, i) => [id, i]));
  const kept = channels.filter((c) =>
    !isInList('hidden', c.id) &&
    !c.categoryIds.some((id) => hiddenCats.has(id)) &&
    (filter.mode !== 'favorites' || isInList('favorite', c.id)) &&
    (!filter.category || c.categoryIds.includes(filter.category)) &&
    (!filter.categories || c.categoryIds.some((id) => filter.categories!.includes(id))) &&
    (!filter.restart || c.archive.advertised));
  if (!order.size) return kept;
  return kept
    .map((c, i) => ({ c, k: order.has(c.id) ? order.get(c.id)! : order.size + i }))
    .sort((a, b) => a.k - b.k)
    .map((x) => x.c);
}

export const MAX_ORDER = 20_000;
export const MAX_PINNED = 50;

/**
 * EPG01 custom order: puts `id` before `target` (null = top) or after it, and returns the channelOrder to store — the
 * shortest prefix that differs from provider order; the rest follows in provider order (see arrangeChannels).
 */
export function moveChannel(providerIds: readonly string[], order: readonly string[], id: string, target: string | null, after = false): string[] {
  const known = new Set(providerIds);
  const ids = [...new Set([...order.filter((x) => known.has(x)), ...providerIds])].filter((x) => x !== id);
  ids.splice(target === null ? 0 : Math.max(0, ids.indexOf(target) + (after ? 1 : 0)), 0, id);
  let k = ids.length;
  while (k > 0 && ids[k - 1] === providerIds[k - 1]) k--;
  return ids.slice(0, k);
}

/** HOME01: unpins `id`, or pins it at `at` (default last); null when MAX_PINNED are already pinned. */
export function togglePinned(pinned: readonly string[], id: string, at = pinned.length): string[] | null {
  if (pinned.includes(id)) return pinned.filter((x) => x !== id);
  return pinned.length >= MAX_PINNED ? null : [...pinned.slice(0, at), id, ...pinned.slice(at)];
}

/** Pin to Home and custom order actions, saved through preferences.set with an Undo toast (EPG01, HOME01). */
export function useChannelPrefs() {
  const user = useUserState();
  const qc = useQueryClient();
  const prefs = user.state?.preferences ?? null;
  const latest = useRef(prefs);
  latest.current = prefs;
  const pinned = prefs?.pinnedChannelIds ?? [];
  const order = prefs?.channelOrder ?? [];
  const save = (p: Partial<Preferences>) => user.mutate({ op: 'preferences.set', preferences: p });
  const savePins = (ids: string[]) => {
    save({ pinnedChannelIds: ids });
    void qc.invalidateQueries({ queryKey: ['home'] });
  };
  const saveOrder = (ids: string[], message: string) => {
    const prev = order;
    save({ channelOrder: ids });
    toast({ message, action: { label: 'Undo', onClick: () => save({ channelOrder: prev }) } });
  };
  return {
    ready: !!prefs,
    customOrder: order.length > 0,
    isPinned: (id: string) => pinned.includes(id),
    togglePin(c: Channel) {
      const at = pinned.indexOf(c.id);
      const next = togglePinned(pinned, c.id);
      if (!next) {
        toast({ message: `You can pin up to ${MAX_PINNED} channels to Home. Unpin one first.`, tone: 'error' });
        return;
      }
      savePins(next);
      toast({
        message: at < 0 ? `${c.name} pinned to Home` : `${c.name} unpinned from Home`,
        action: {
          label: 'Undo',
          onClick: () => { // against the latest pins, so other pins made since are kept
            const cur = latest.current?.pinnedChannelIds ?? [];
            const back = togglePinned(cur, c.id, at);
            if (back && cur.includes(c.id) === at < 0) savePins(back);
          },
        },
      });
    },
    /** Moves `c` next to `target` in the full channel list; `position` is its new 1-based place in the view, for the toast. */
    move(providerIds: string[], c: Channel, target: string | null, after: boolean, position: string) {
      const next = moveChannel(providerIds, order, c.id, target, after);
      if (next.length > MAX_ORDER) {
        toast({ message: `Custom order covers the first ${MAX_ORDER.toLocaleString()} channels only.`, tone: 'error' });
        return;
      }
      saveOrder(next, `Moved ${c.name} to ${position}`);
    },
    resetOrder: () => saveOrder([], 'Channels are back in provider order'),
  };
}

export type RowGuide = { status: 'loading' } | { status: 'error' } | { status: 'ready'; programs: Program[]; mapped: boolean };
export type GuideBlocks = { rows: Map<string, RowGuide>; unavailable: boolean; stale: { fetchedAt: string | null } | null; error: unknown };

/** Guide for the channels in [firstIndex, lastIndex] of `channels`, fetched in blocks of 50. Cached 5 min (XC04). */
export function useGuideBlocks(connectionId: string | undefined, channels: Channel[], firstIndex: number, lastIndex: number, from: string, to: string): GuideBlocks {
  const blocks: string[][] = [];
  if (channels.length) {
    const b0 = Math.floor(Math.max(0, firstIndex) / GUIDE_BLOCK);
    const b1 = Math.floor(Math.min(channels.length - 1, Math.max(0, lastIndex)) / GUIDE_BLOCK);
    for (let b = b0; b <= b1; b++) blocks.push(channels.slice(b * GUIDE_BLOCK, (b + 1) * GUIDE_BLOCK).map((c) => c.id));
  }
  const results = useQueries({
    queries: blocks.map((ids) => ({
      queryKey: ['guide', connectionId, from, to, ids.join(',')],
      enabled: !!connectionId && ids.length > 0,
      staleTime: 5 * 60_000,
      placeholderData: undefined,
      // EPG03: a channel whose provider lookup was still running when the server answered is `pending`; ask again shortly.
      refetchInterval: (q: { state: { data?: GuideResponse } }) => (q.state.data?.channels.some((g) => g.pending) ? 4000 : false),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api<GuideResponse>('GET', `/api/guide${qs({ connectionId, channelIds: ids.join(','), from, to })}`, undefined, { signal }),
    })),
  });
  const rows = new Map<string, RowGuide>();
  let unavailable = false;
  let stale: GuideBlocks['stale'] = null;
  let error: unknown = null;
  results.forEach((r, i) => {
    const ids = blocks[i]!;
    if (r.data) {
      if (r.data.status === 'unavailable') unavailable = true;
      if (r.data.freshness.stale) stale = { fetchedAt: r.data.freshness.fetchedAt };
      const byId = new Map(r.data.channels.map((g) => [g.channelId, g]));
      for (const id of ids) {
        const g = byId.get(id);
        if (g?.pending && !g.programs.length) { rows.set(id, { status: 'loading' }); continue; }
        const programs = (g?.programs ?? []).slice().sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
        rows.set(id, { status: 'ready', programs, mapped: !!g && g.mapping !== 'unmapped' });
      }
    } else if (r.isError) {
      const code = (r.error as { code?: string }).code;
      if (code === 'GUIDE_UNAVAILABLE') { unavailable = true; ids.forEach((id) => rows.set(id, { status: 'ready', programs: [], mapped: false })); }
      else { error = r.error; ids.forEach((id) => rows.set(id, { status: 'error' })); }
    } else ids.forEach((id) => rows.set(id, { status: 'loading' }));
  });
  return { rows, unavailable, stale, error };
}

/** Program airing at `t` (ms) in a sorted list, and the one after it. */
export function nowNext(programs: Program[], t: number): { now: Program | null; next: Program | null } {
  const i = programs.findIndex((p) => Date.parse(p.end) > t);
  if (i < 0) return { now: null, next: null };
  const p = programs[i]!;
  return Date.parse(p.start) <= t ? { now: p, next: programs[i + 1] ?? null } : { now: null, next: p };
}
